import React, { useState, useEffect } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { useConfirm } from '../ui/ConfirmDialog';

const DepartmentsSettings = () => {
  const confirm = useConfirm();
  const [departments, setDepartments] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [draft, setDraft] = useState({ name: '', managerId: '' });
  const [saving, setSaving] = useState(false);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [deps, members] = await Promise.all([
        axios.get('/api/departments'),
        axios.get('/api/teams/members').catch(() => ({ data: [] }))
      ]);
      setDepartments(deps.data.departments || []);
      setTeamMembers(Array.isArray(members.data) ? members.data : (members.data.members || []));
    } catch (e) {
      toast.error('Failed to load departments');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadAll(); }, []);

  const openCreate = () => {
    setEditing(null);
    setDraft({ name: '', managerId: '' });
    setEditorOpen(true);
  };

  const openEdit = (d) => {
    setEditing(d);
    setDraft({ name: d.name || '', managerId: d.manager_id || '' });
    setEditorOpen(true);
  };

  const close = () => {
    setEditorOpen(false);
    setEditing(null);
  };

  const save = async () => {
    if (!draft.name.trim()) { toast.error('Name is required'); return; }
    setSaving(true);
    try {
      if (editing) {
        await axios.put(`/api/departments/${editing.id}`, {
          name: draft.name,
          managerId: draft.managerId || null
        });
        toast.success('Department updated');
      } else {
        await axios.post('/api/departments', {
          name: draft.name,
          managerId: draft.managerId || null
        });
        toast.success('Department created');
      }
      await loadAll();
      close();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to save department');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (d) => {
    const ok = await confirm({
      title: `Delete department "${d.name}"?`,
      confirmLabel: 'Delete',
      destructive: true
    });
    if (!ok) return;
    try {
      await axios.delete(`/api/departments/${d.id}`);
      toast.success('Department deleted');
      await loadAll();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to delete');
    }
  };

  return (
    <div className="bg-white dark:bg-gray-800 shadow rounded-lg overflow-hidden">
      <div className="p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-lg font-medium text-gray-900 dark:text-white">Departments</h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              Departments group your team for display, filtering, and approval routing.
              If a department has a manager, leave requests from its members go to that
              manager (with fallback to team admins when none is set).
            </p>
          </div>
          <button
            onClick={openCreate}
            className="inline-flex py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700"
          >
            Create Department
          </button>
        </div>

        {loading ? (
          <p className="text-gray-500 dark:text-gray-400">Loading…</p>
        ) : departments.length === 0 ? (
          <div className="bg-gray-50 dark:bg-gray-700 p-6 rounded-md text-center">
            <p className="text-gray-600 dark:text-gray-300">No departments yet.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Name</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Manager</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Members</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {departments.map(d => (
                  <tr key={d.id}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">{d.name}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300">
                      {d.manager ? `${d.manager.firstName} ${d.manager.lastName}` : '—'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300">{d.memberCount || 0}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium space-x-3">
                      <button onClick={() => openEdit(d)} className="text-primary-600 hover:text-primary-800 dark:text-primary-400">Edit</button>
                      <button onClick={() => remove(d)} className="text-red-600 hover:text-red-900 dark:text-red-400">Delete</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {editorOpen && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 z-50 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full">
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                {editing ? 'Edit Department' : 'Create Department'}
              </h3>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Name</label>
                <input
                  type="text"
                  value={draft.name}
                  onChange={e => setDraft({ ...draft, name: e.target.value })}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white sm:text-sm"
                  placeholder="e.g., Engineering"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Manager (optional)</label>
                <select
                  value={draft.managerId || ''}
                  onChange={e => setDraft({ ...draft, managerId: e.target.value })}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white sm:text-sm"
                >
                  <option value="">No manager</option>
                  {teamMembers.map(m => (
                    <option key={m.id} value={m.id}>{m.firstName} {m.lastName}</option>
                  ))}
                </select>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  If set, leave requests from this department&apos;s members will be
                  routed to the manager for approval. Leave blank to fall back to team admins.
                </p>
              </div>
            </div>
            <div className="px-6 py-3 bg-gray-50 dark:bg-gray-700 flex justify-end space-x-2">
              <button onClick={close} className="py-2 px-4 border border-gray-300 dark:border-gray-500 rounded-md text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50">Cancel</button>
              <button
                onClick={save}
                disabled={saving}
                className="py-2 px-4 border border-transparent rounded-md text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50"
              >
                {saving ? 'Saving…' : (editing ? 'Save Changes' : 'Create')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DepartmentsSettings;
